import {
  deleteDataFromServer,
  getDataFromServer,
  postDataToServer,
  putDataToServer
} from '../../util';

/**
 * Spatial View — Event Field Configuration.
 *
 * Seven endpoints, mounted by the BE at `/eventFieldConfig` inside the COMMON
 * context (CommonControllerHandler: `context.mount(new EventFieldConfigController
 * (...), "/eventFieldConfig", ...)`), giving `/amls/common/eventFieldConfig/...`.
 * All seven are AAA-registered in CommonAaaResourcesHandler — reads take
 * `commonScopeRead`, writes `commonScopeWrite`.
 *
 * THE URLs ARE OWNED HERE AND NOWHERE ELSE. The screen and its components take
 * data as props and never build a path, which is what keeps a contract change to
 * one file.
 *
 * ERROR HANDLING IS NOT OPTIONAL HERE. `getDataFromServer` RETURNS its error
 * instead of throwing (`return err` in its catch), so an unchecked caller renders
 * an Error object as if it were data — on this screen that would be a field list.
 * Every read below therefore goes through `unwrap`.
 */
class EventFieldConfigModule {
  constructor(baseURL, httpHeaders) {
    // AMLS_COMMON ('/common'), the same base the event timeline uses.
    this.baseURL = `${baseURL}/eventFieldConfig`;
    this.httpHeaders = httpHeaders;
  }

  /**
   * Turns the "error as a value" shape into a thrown error.
   *
   * A 404 is passed through as `null` rather than thrown: it means the
   * controller is not mounted on this deployment (the field-config branch is not
   * merged everywhere yet), and the screen should show a "not enabled" state
   * rather than an error. Same reasoning as ScoringConfigModule's decay policy.
   */
  static unwrap(response, what) {
    // THE STATUS IS NOT WHERE IT LOOKS LIKE IT IS. `getDataFromServer` returns
    // the axios ERROR object on failure (`return err` in its catch), and axios
    // puts the code on `err.response.status` — not `err.status`. Reading
    // `response.status` alone found `undefined`, fell through, and handed
    // Tomcat's HTML 404 page back as if it were data: the screen then rendered an
    // empty list instead of falling back. Measured against a deployment without
    // the controller mounted, 2026-08-31.
    const status = response?.response?.status ?? response?.status;

    // A MOUNTED controller answers 404 with its OWN JSON body — `delete` and the
    // per-type reads return `{"error":"Event type 42 not found"}`. An UNMOUNTED
    // one gets Tomcat's HTML page, which has no `error` key. Only the latter
    // means "not enabled on this deployment", so a real not-found is rethrown
    // instead of being reported to the admin as a missing feature.
    if (status === 404) {
      const missing = response?.response?.data ?? response?.data;
      if (missing?.error) throw new Error(missing.error);
      return null;
    }

    if (response instanceof Error || (status && status !== 200)) {
      // `error` FIRST — that is the key this API uses. A rejected save returns
      //     {"error":"Maximum 3 fields can be selected for Spatial Layout"}
      // and reading only `message` swallowed it, so every validation failure
      // surfaced as the generic "Failed to save the fields" with no clue what
      // was wrong (reported 2026-09-03 against XBORDER, which had 4 spatial
      // fields against a cap of 3). `message` is kept as a fallback for any
      // handler that uses the other convention.
      const body = response?.response?.data ?? response?.data;
      throw new Error(
        body?.error || body?.message || response?.message || `Failed to ${what}`
      );
    }

    // 204 NO CONTENT. `delete` answers `NoContent()` on success and
    // `deleteDataFromServer` returns `res.data`, which axios materialises as the
    // EMPTY STRING. The string guard below was written for Tomcat's HTML error
    // page and rejected that too — so a delete that had ALREADY removed the row
    // surfaced as "Failed to delete the event type", and because the caller's
    // `.then` never ran the list was never refetched and the deleted type stayed
    // on screen. Reported 2026-09-03. `undefined` and not `null`: `null` is the
    // not-mounted signal above, which the caller reports as
    // "Deleting is not available on this environment yet."
    if (response === '') return undefined;

    // A STRING is never a valid payload here — every endpoint returns JSON. This
    // catches the case above even if the error shape changes again: an HTML error
    // page arrives as a string and must not reach an adapter.
    if (typeof response === 'string') {
      throw new Error(`Failed to ${what}`);
    }

    return response;
  }

  // ── Event types ────────────────────────────────────────────────────────────

  getEventTypes = async (category) =>
    EventFieldConfigModule.unwrap(
      await getDataFromServer(
        `${this.baseURL}/event-types`,
        this.httpHeaders,
        // ACTIVITY | TRANSACTION — the BE's `event_category` enum.
        category ? { category } : {}
      ),
      'load event types'
    );

  createEventType = async (payload) =>
    EventFieldConfigModule.unwrap(
      await postDataToServer(`${this.baseURL}/event-types`, payload),
      'create the event type'
    );

  updateEventType = async (id, payload) =>
    EventFieldConfigModule.unwrap(
      await putDataToServer(
        `${this.baseURL}/event-types/${encodeURIComponent(id)}`,
        this.httpHeaders,
        {},
        payload
      ),
      'update the event type'
    );

  // FIN-12858 deliberately did NOT convert this one. The sweep moved every other delete onto
  // `deleteDataFromServerWithFullResponse` + `throwIfDeleteFailed` so the HTTP status is
  // visible. `unwrap` below is built around receiving the BODY: `response === ''` is how it
  // recognises a 204, and a bare string is how it recognises Tomcat's HTML error page. Handing
  // it a full axios response would break both — a 204 delete would start throwing, which is
  // the exact regression fixed here on 2026-09-03. Its 404-means-controller-not-mounted
  // behaviour is also intentional and load-bearing.
  //
  // Making this one status-aware means reworking `unwrap` itself, which serves every read on
  // the screen, not just delete. Tracked separately.
  deleteEventType = async (id) =>
    EventFieldConfigModule.unwrap(
      await deleteDataFromServer(
        `${this.baseURL}/event-types/${encodeURIComponent(id)}`,
        {}
      ),
      'delete the event type'
    );

  // ── Fields ─────────────────────────────────────────────────────────────────

  /** The picker's catalogue. Category-scoped, not per event type. */
  getAvailableFields = async (category) =>
    EventFieldConfigModule.unwrap(
      await getDataFromServer(
        `${this.baseURL}/event-fields/available`,
        this.httpHeaders,
        category ? { category } : {}
      ),
      'load the available fields'
    );

  getEventTypeFields = async (id) =>
    EventFieldConfigModule.unwrap(
      await getDataFromServer(
        `${this.baseURL}/event-types/${encodeURIComponent(id)}/fields`,
        this.httpHeaders
      ),
      'load the configured fields'
    );

  /**
   * Saves the WHOLE list, not a delta — the BE takes `SaveFieldsRequest(fields)`
   * and replaces. Sending a subset silently deletes the rest.
   */
  saveEventTypeFields = async (id, fields) =>
    EventFieldConfigModule.unwrap(
      await putDataToServer(
        `${this.baseURL}/event-types/${encodeURIComponent(id)}/fields`,
        this.httpHeaders,
        {},
        { fields }
      ),
      'save the fields'
    );
}

export default EventFieldConfigModule;
